/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useEffect, useState } from "react";

import { sendGTMEvent } from "@next/third-parties/google";
import { SubscriptionPlans } from "@prisma/client";
import { posthog as posthogClient } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import { env } from "@musicfy/env.mjs";

if (typeof window !== "undefined") {
  const baseUrl =
    env.NEXT_PUBLIC_VERCEL_ENV === "production"
      ? "https://create.musicfy.lol"
      : window.location.origin;
  posthogClient.init("phc_b1IjIo9iTOKwDxiCve7abg82mC4SpKQQqz8hDe1qElR", {
    api_host: `${baseUrl}/ingest`,
    autocapture: false,
    disable_session_recording: true,
    disable_scroll_properties: true,
  });
}

const AnalyticsContext = createContext<IAnalyticsContext>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackEvent: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  identify: () => {},
});

export function useAnalytics() {
  const analyticsContext = useContext(AnalyticsContext);

  if (!analyticsContext) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }

  return analyticsContext;
}

interface IAnalyticsContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackEvent: (event: string, properties: Record<string, any>) => void;
  identify: (
    userId: string,
    email: string,
    subscriptionPlan?: SubscriptionPlans | undefined,
    marketingOptIn?: boolean
  ) => void;
}

const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] =
    useState<SubscriptionPlans | null>(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const trackEvent = (event: string, properties: Record<string, any>) => {
    if (typeof window === "undefined") return;

    posthogClient.capture(event, properties);
    sendGTMEvent({
      event: event,
      userId: identifier,
      email: email,
      subscription: subscriptionPlan ?? SubscriptionPlans.free,
      marketingOptIn,
      ...properties,
    });
    if (typeof window !== "undefined" && window.clarity && identifier) {
      window.clarity("event", event);
    }
  };

  const identify = (
    userId: string,
    email: string,
    subscriptionPlan?: SubscriptionPlans | undefined,
    hasMarketingOptIn = false
  ) => {
    setIdentifier(userId);
    setEmail(email);
    setSubscriptionPlan(subscriptionPlan ?? SubscriptionPlans.free);
    setMarketingOptIn(hasMarketingOptIn);

    sendGTMEvent({
      event: "identify",
      userId: userId,
      email: email,
      subscription: subscriptionPlan ?? SubscriptionPlans.free,
      marketingOptIn: hasMarketingOptIn,
    });
    posthogClient?.identify(
      userId,
      {
        subscription: subscriptionPlan ?? SubscriptionPlans.free,
        marketingOptIn: hasMarketingOptIn,
      },
      {
        email: email,
      }
    );
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("identify", userId);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.dataLayer) return;

    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function gtag(...args: any[]) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      window.dataLayer?.push(...args);
    }

    gtag("consent", "default", {
      ad_user_data: "granted",
      ad_personalization: "granted",
      ad_storage: "granted",
      analytics_storage: "granted",
    });

    window.clarity?.("consent");
  }, []);

  return (
    <AnalyticsContext.Provider value={{ trackEvent, identify }}>
      <PostHogProvider client={posthogClient}>{children}</PostHogProvider>
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
